import { observable, action, computed, makeObservable, toJS } from 'mobx';

import office from 'src/services/office';
import { SequentialTasksExecutor, NotActualTaskError } from 'src/services/sequentialTasksExecutor';

import { mergeLists, MergeListsAction, asArray, asString } from 'shared/utils/utils';
import * as QA from 'shared/utils/queryAnalysis';
import { RangeAddress } from 'shared/excel/rangeAddress';
import { runCancelableAction } from 'src/services/utils';
import { executeFlows } from 'src/services/execution';


const Data = {
  Fields: [QA.FieldType.Picklist, QA.FieldType.Lookup],
  DefaultContext: { id: '', name: '', flowsteps: [], assignedLookups: [] },
};

const MetaFeatures = {
  Basic: 1 << 0,
  Picklists: 1 << 1,
  Lookups: 1 << 2,
  get Context() { return this.Picklist | this.Lookups; }
};


export default class ContextStore {
  @observable worksheets = [];

  @observable activeWorksheetContext = Data.DefaultContext;
  @observable worksheetsMeta = new Map();
  @observable loadContext = false;
  @observable isLoading = false;
  @observable isLoaded = false;
  @observable namedRanges = [];
  @observable assignedLookups = [];
  @observable previousSelectedRanges = null;

  requestStore = null;
  userStore = null;
  dataStore = null;
  salesforceStore = null;

  contextUpdateTasks = new SequentialTasksExecutor();
  lastSelectionToken = null;

  @computed get flowSteps() { return this.dataStore.flowSteps; }

  @computed get worksheetsIdsByName() {
    return new Map(Array.from(this.worksheetsMeta.entries()).map(([sheetId, sheet]) => [sheet.name, sheetId]))
  }

  @computed({ keepAlive: true })
  get runButtons() {
    const res = new Map(this.dataStore.flows.reduce((buttons, flow) => {
      const { address, customRange } = flow.options.runButtonCell || {};
      let { worksheetId } = flow.options.runButtonCell || {};
      let rangeAddress;

      if (customRange) {
        rangeAddress = this.getNamedRangeAddress(customRange);
        worksheetId = rangeAddress ? this.worksheetsIdsByName.get(rangeAddress.worksheetName) : undefined;
      }
      if (address && worksheetId) {
        rangeAddress = new RangeAddress(address);
      }
      const cells = rangeAddress?.getAllCells() || [];

      cells.forEach(cell => buttons.push([`${worksheetId}!${cell.shortAddress}`, flow]));
      return buttons;
    }, []));

    return res
  }

  @computed
  get assignedLookupsLastId() {
    const firstId = 0;
    return this.assignedLookupLastId = this.assignedLookups.reduce((maxId, lookup) => {
      return Math.max(maxId, Number(lookup.rangeName?.split("_")[2]) | firstId);
    }, firstId);
  }

  constructor() {
    makeObservable(this);
  }

  linkStores(stores) {
    Object.assign(this, stores);
  }

  @action async clean() {
    this.contextUpdateTasks = new SequentialTasksExecutor();
    this.worksheetsMeta = new Map();
    this.previousSelectedRanges = null;
    await this.updateWorksheets();
    this.isLoaded = false;
  }

  @action async startup() {
    await this.subscribeToEvents();
    await this.updateWorksheets();
    await this.loadAssignedLookups();
    this.isLoaded = true;
  }

  @action async subscribeToEvents() {
    await office.subscribeWorksheetsEvents((worksheets) => {
      worksheets.onActivated.add(this.handleWorksheetActivated);
      worksheets.onAdded.add(this.updateWorksheets);
      worksheets.onDeleted.add(this.updateWorksheets);
      worksheets.onSelectionChanged.add(this.handleSelectionChanged);
      worksheets.onSingleClicked.add(this.handleCellClicked);
      worksheets.onChanged.add(this.handleWorksheetChanges);
    });
  }

  @action updateWorksheets = async (event) => {
    const worksheets = await office.getAllWorksheets();
    this.worksheets = worksheets;

    if (event?.type === "WorksheetDeleted") {
      const flowstepsToDelete = this.dataStore.flowSteps.filter(flowstep =>
        flowstep.worksheetId == event.worksheetId || flowstep.options.worksheetId === event.worksheetId
      )
      this.dataStore.updateFlowstepList(flowstepsToDelete, MergeListsAction.Remove);
      await this.dataStore.syncOfficeAndSchedulerWithStore();
    }

    await this.updateActiveWorksheetContext();
  }

  @action handleWorksheetActivated = async (event) => {
    // console.log('ContextStore::OnActivatedEvent: ', event);
    await this.updateActiveWorksheetContext();
  }

  @action handleSelectionChanged = async (event) => {
    // console.log(`ContextStore::OnSelectionChangedEvent: WsId: ${event.worksheetId}, ranges: ${event.address}`);

    if (this.activeWorksheetContext.selectedRanges) {
      this.previousSelectedRanges = {
        worksheetId: this.activeWorksheetContext.id,
        ranges: this.activeWorksheetContext.selectedRanges
      };
    }

    await this.updateSelectedWorksheetContext(event.worksheetId, event.address.split(',').map(x => new RangeAddress(x)));
  }

  isExecutionRunning = false;

  handleCellClicked = async ({ address, worksheetId }) => {
    if (this.isExecutionRunning) {
      return;
    }

    this.isExecutionRunning = true;
    try {
      const flow = this.runButtons.get(`${worksheetId}!${address}`);
      if (flow) {
        console.log(`ContextStore::OnSingleClickedEvent: executing flow ${flow.name} at ${worksheetId}!${address}`);
        await Office.addin.showAsTaskpane();
        await runCancelableAction(async (token) => {
          if (this.previousSelectedRanges) {
            await office.selectRange(this.previousSelectedRanges.worksheetId, this.previousSelectedRanges.ranges.map(r => r.shortAddress));
          }

          await executeFlows(asArray(flow), token, { withoutDialogs: true, selectedRanges: this.previousSelectedRanges && toJS(this.previousSelectedRanges) });
        });
      }
    } finally {
      this.isExecutionRunning = false;
    }
  }

  handleWorksheetChanges = async ({ changeType }) => {
    if (["RowInserted", "RowDeleted", "ColumnInserted", "ColumnDeleted", "CellInserted", "CellDeleted"].includes(changeType)) {
      // Prevent calculation during flowstep execution. Flowsteps will trigger the update themselves after their execution
      if (!this.requestStore.activeTask) {
        await this.updateNamedRanges();
      }
    }
  }

  @action async updateActiveWorksheetContext() {
    // console.log('ContextStore::updateActiveWorksheetContext');
    this.activeWorksheetContext = { ...this.activeWorksheetContext, flowsteps: [], assignedLookups: [] };
    const { addresses, worksheetId } = await office.getSelectedRanges();
    await this.updateSelectedWorksheetContext(worksheetId, addresses);
  }

  @action async reloadWorksheetContext(idOrIds) {
    const ids = asArray(idOrIds);
    ids.forEach(x => this.worksheetsMeta.delete(x));

    // console.log('ContextStore::reloadWorksheetContext', ids);
    const { addresses, worksheetId } = await office.getSelectedRanges();
    if (ids.includes(worksheetId)) {
      await this.updateSelectedWorksheetContext(worksheetId, addresses);
    }
  }

  @action async updateSelectedWorksheetContext(worksheetId, selectedRanges) {
    // console.log('ContextStore::updateSelectedWorksheetContext: ', selectedRanges);

    // It's is not enough to check only for token.throwIfNotActual, because at some point we get to
    // this.fetchWorksheetMeta, where Excel.run might have been already started.
    // For some reason, sequential calls to it slow down Excel soooooo much
    // that after 20+ fast selections of different cells with
    // arrow keys it can take more than 30+ seconds to finally process all calls to Excel.run,
    // even though there is a token.throwIfNotActual() check in the very beginning of it.
    // So, with SequentialTaskExecutor we wait until all previous tasks are finished, then check,
    // whether there're newer tasks, if not, run it
    await this.contextUpdateTasks.add(async (token) => {
      try {
        this.isLoading = true;
        token.throwIfNotActual();

        const meta = await this.updateWorksheetMeta(worksheetId, token);
        token.throwIfNotActual();

        const context = await this.getWorksheetContext(meta, selectedRanges);
        // console.log('ContextStore::updateSelectedWorksheetContext: context: ', context);
        token.throwIfNotActual();

        this.activeWorksheetContext = context;
      }
      catch (err) {
        if (!(err instanceof NotActualTaskError)) {
          console.error('ContextStore::updateSelectedWorksheetContext: Error: ', err.message);
        }

        if (token.isActual) {
          this.activeWorksheetContext = Data.DefaultContext;
        }
      }
      finally {
        this.isLoading = false;
      }
    });
  }

  @action async updateWorksheetMeta(worksheetId, token) {
    let meta = this.worksheetsMeta.get(worksheetId);
    let updateMeta = true;

    if (!meta || (this.loadContext && !(meta.features & MetaFeatures.Context))) {
      const featuresToFetch = MetaFeatures.Basic
        | (this.loadContext ? MetaFeatures.Context : 0)
        ;
      meta = await this.fetchWorksheetMeta(worksheetId, featuresToFetch, token);
    }
    else if (this.loadContext) { // We need to track changes in tables/ranges if user adds/removes rows/columns
      meta = await this.updateRangesInMeta(meta, token);
    }
    else {
      updateMeta = false;
    }

    token.throwIfNotActual();
    updateMeta && this.worksheetsMeta.set(worksheetId, meta);

    return meta;
  }

  @action
  async updateNamedRanges() {
    const officeNamedItems = await office.getNamedItems();
    const ranges = officeNamedItems.filter(item => item.isRange);
    this.namedRanges = ranges;
  }

  @action
  async loadAssignedLookups() {
    await this.updateNamedRanges();
    this.assignedLookups = await office.getAssignedLookups();
  }

  @action
  async updateAssignedLookups(newLookups) {
    await office.replaceAssignedLookups(newLookups);
    this.assignedLookups = newLookups;
    await this.reloadWorksheetContext(this.activeWorksheetContext.id);
  }

  async saveAssingedLookup(lookup) {
    const newLookups =
      mergeLists(this.assignedLookups, [lookup],
        MergeListsAction.ReplaceAndMerge, (l1, l2) => l1.id === l2.id);
    await this.updateAssignedLookups(newLookups);
  }

  async deleteAssignedLookup(lookupId) {
    const newLookups = [];
    for (const lookup of this.assignedLookups) {
      if (lookup.id !== lookupId) {
        newLookups.push(lookup);
      }
      else {
        if (lookup.rangeName) {
          await office.deleteNamedRange(lookup.rangeName);
        }
        if (lookup.idCellAddress?.customRange) {
          await office.deleteNamedRange(lookup.idCellAddress.customRange);
        }
      }
    }
    await this.updateAssignedLookups(newLookups);
  }

  async fetchWorksheetMeta(worksheetId, features, token) {
    return await Excel.run(async (ctx) => {
      const worksheet = ctx.workbook.worksheets.getItem(worksheetId).load(['name']);

      token.throwIfNotActual();
      await ctx.sync();
      token.throwIfNotActual();

      const baseWorksheetMeta = { id: worksheet.id, name: worksheet.name, features };

      if (!(features & MetaFeatures.Context)) {
        return { ...baseWorksheetMeta, flowsteps: [], assignedLookups: [] };
      }

      const flowsteps = await this.fetchWorksheetFlowsteps(worksheet, token);

      const assignedLookups = await this.fetchWorksheetAssignedLookups(worksheet, token);
      return { ...baseWorksheetMeta, flowsteps, assignedLookups };
    });
  }

  async fetchWorksheetFlowsteps(worksheet, token) {
    const worksheetFlowsteps = this.flowSteps.filter(x => x.worksheetId === worksheet.id);
    if (!worksheetFlowsteps.length) {
      return [];
    }

    const nonEmptyFlowsteps = [];

    for (const flowstep of worksheetFlowsteps) {
      const { target, id, name, type } = flowstep;

      const rawColumns = await this.salesforceStore.getFlowstepColumns(flowstep);
      this.convertRichColumns(rawColumns);
      token.throwIfNotActual();

      if (!rawColumns.length) {
        continue;
      }

      const filteredColumns = await this.filterColumns(worksheet, target, rawColumns);
      token.throwIfNotActual();

      // If pull and push are on the same target, merge their columns
      const sameTarget = nonEmptyFlowsteps.find(x => x.target === target);
      if (sameTarget) {
        const columnsComparator = (a, b) => a.columnIndex === b.columnIndex;
        sameTarget.columns = mergeLists(sameTarget.columns, filteredColumns,
          MergeListsAction.Merge, columnsComparator)
          .sort((a, b) => a.columnIndex - b.columnIndex);
        sameTarget.rawColumns = mergeLists(sameTarget.rawColumns, rawColumns,
          MergeListsAction.Merge, columnsComparator);
      }
      else {
        nonEmptyFlowsteps.push({ id, name, target, type, columns: filteredColumns, rawColumns });
      }

      // console.log('ContextStore::fetchWorksheetFlowsteps: out: ', nonEmptyFlowsteps[nonEmptyFlowsteps.length - 1]);
    }
    return nonEmptyFlowsteps;
  }

  async fetchWorksheetAssignedLookups(worksheet, token) {
    const worksheetLookups = this.assignedLookups.filter(lookup => lookup.worksheetId === worksheet.id);
    if (worksheetLookups.length === 0) {
      return [];
    }

    const result = [];

    for (const lookup of worksheetLookups) {
      token.throwIfNotActual();
      const { range, rangeParams } = await this.filterLookup(lookup);
      token.throwIfNotActual();
      if (range) {
        const rawField = await this.salesforceStore.getAssignedLookupField(lookup);
        token.throwIfNotActual();
        if (rawField) {
          const rawData = {
            lookupId: lookup.id,
            rawField: { ...rawField, type: QA.FieldTypeName.Id },
            fieldName: QA.toCanonicalForm(lookup.fieldName),
            objectName: lookup.objectName,
            idCellAddress: lookup.idCellAddress,
            idCellColumn: lookup.idColumnId && {
              idColumnId: lookup.idColumnId,
              tableId: lookup.tableId,
              lookupColumnId: lookup.columnId
            }

          };

          const column = {
            columnData: rawData,
            range,
            headerName: `${lookup.objectName} by ${lookup.fieldName}`,
          };
          if (rangeParams) {
            column.rangeParams = rangeParams;
          }

          result.push(column);
        }
      }
    }

    return result;
  }

  async updateRangesInMeta(meta, token) {
    const { id: worksheetId, flowsteps, assignedLookups } = meta;

    let updatedFlowsteps = flowsteps, updatedLookups = assignedLookups;

    if (flowsteps.length) {
      await Excel.run(async (ctx) => {
        const worksheet = ctx.workbook.worksheets.getItem(worksheetId);
        await ctx.sync();
        token.throwIfNotActual();
        updatedFlowsteps = await this.updateFlowstepsRanges(worksheet, flowsteps, token);
      });
    }

    token.throwIfNotActual();

    if (assignedLookups.length) {
      updatedLookups = await this.updateAssignedLookupsRanges(assignedLookups, token);
    }

    const updatedMeta = { ...meta, flowsteps: updatedFlowsteps, assignedLookups: updatedLookups };
    return updatedMeta;
  }

  async updateFlowstepsRanges(worksheet, flowsteps, token) {
    const updatedFlowsteps = [];
    for (const flowstep of flowsteps) {
      token.throwIfNotActual();

      const { target, rawColumns } = flowstep;

      const columns = await this.filterColumns(worksheet, target, rawColumns);
      token.throwIfNotActual();

      if (columns.length) {
        updatedFlowsteps.push({ ...flowstep, columns });
      }
    }

    return updatedFlowsteps;
  }

  async updateAssignedLookupsRanges(lookups, token) {
    const updatedLookups = [];
    for (const lookup of lookups) {
      token.throwIfNotActual();
      const { range } = await this.filterLookup(lookup)
      token.throwIfNotActual();
      if (range) {
        updatedLookups.push({ ...lookup, range });
      }
    }

    return updatedLookups;
  }



  async filterColumns(worksheet, target, rawColumns) {
    const dataTable = await office.getDataTable(worksheet.getRange(target));
    if (!dataTable) {
      // console.log(`No data table found for ${flowstep.name} at ${worksheet.name}!${target}`)
      return [];
    }

    const headers = await dataTable.getHeaders();
    const { rowIndex, columnIndex: startColumnIndex, rowCount: dbrRowCount } = dataTable.dataBodyRange;
    const rowCount = dbrRowCount + (dataTable.isRealTable || !this.enableRangeTracking ? 0 : 1);

    const filteredColumns = rawColumns.reduce((out, columnData) => {
      const { columnIndex } = columnData;
      if (headers.length > columnIndex) {
        out.push({
          range: new RangeAddress({
            rowIndex, columnIndex: startColumnIndex + columnIndex,
            rowCount, columnCount: 1,
          }),
          headerName: headers[columnIndex], columnIndex,
          columnData,
        });
      }
      return out;
    }, []);

    return filteredColumns;
  }

  async filterLookup(lookup) {
    const { address, tableId, columnId, rangeName } =
      (
        lookup.rangeParams ||
        //backward combability
        lookup
      );
    const rangeParams = { address, tableId, columnId, rangeName };

    if (rangeName) {
      const range = this.getNamedRangeAddress(rangeName);
      return { rangeParams, range }
    }
    if (address) {
      return { rangeParams, range: address };
    }
    else if (tableId && columnId) {
      const { table, columns } = await office.getTable(tableId);
      if (!table || !columns) {
        return {};
      }
      for (let column of columns) {
        if (column.id === columnId) {
          const range = column.getRange();
          range.load('address');
          await range.context.sync();
          return {
            rangeParams,
            range: new RangeAddress(range.address)
          };
        }
      }


    }
    return {};
  }

  async convertRichColumns(columns) {
    columns.forEach(({ rawField }) => {
      if (QA.isRichTextField(rawField)) {
        rawField.type = QA.FieldTypeName.RichText;
      }
    });
  }

  async getWorksheetContext(worksheetMeta, selectedRanges) {
    const flowsteps = this.loadContext
      ? await this.filterFlowstepsBySelection(worksheetMeta, selectedRanges)
      : [];
    const assignedLookups = this.loadContext
      ? await this.filterAssignedLookupsBySelection(worksheetMeta, selectedRanges)
      : [];
    return { ...worksheetMeta, flowsteps, assignedLookups, selectedRanges };

  }

  async filterFlowstepsBySelection({ flowsteps: initialFlowsteps, id: worksheetId }, selectedRanges) {
    return await Excel.run(async (ctx) => {
      const worksheet = ctx.workbook.worksheets.getItem(worksheetId);
      let needToLoadValues = false;

      const filterColumns = (columns) => columns.reduce((filtered, column) => {
        const intersections = selectedRanges.map(range => range.intersect(column.range)).filter(Boolean);
        const isSingleCell = intersections.length === 1 && intersections[0].isSingleCell;
        if (isSingleCell) {
          needToLoadValues = true;
        }

        intersections.length && filtered.push({
          ...column,
          selection: {
            intersections, isSingleCell,
            cellValue: isSingleCell ? worksheet.getRange(intersections[0].shortAddress).load(['values']) : undefined,
          }
        });
        return filtered;
      }, []);

      const flowsteps = initialFlowsteps.reduce((filteredFlowsteps, { columns, ...flowstep }) => {
        const filteredColumns = filterColumns(columns);

        if (filteredColumns.length) {
          filteredFlowsteps.push({ ...flowstep, columns: filteredColumns });
        }
        return filteredFlowsteps;
      }, []);

      if (needToLoadValues) {
        await ctx.sync();

        flowsteps.forEach(flowstep =>
          flowstep.columns.forEach(({ selection }) => selection.cellValue = asString(selection.cellValue?.values[0][0]))
        );
      }

      return flowsteps;
    });
  }


  async filterAssignedLookupsBySelection({ assignedLookups: initialLookups, id: worksheetId }, selectedRanges) {
    return await Excel.run(async (ctx) => {
      const worksheet = ctx.workbook.worksheets.getItem(worksheetId);
      let needToLoadValues = false;

      const lookups = initialLookups.reduce((filteredLookups, lookup) => {
        const intersections = selectedRanges.map(range => range.intersect(lookup.range)).filter(Boolean);
        const isSingleCell = intersections.length === 1 && intersections[0].isSingleCell;
        if (isSingleCell) {
          needToLoadValues = true;
        }
        if (intersections.length) {
          filteredLookups.push({
            ...lookup,
            selection: {
              intersections, isSingleCell,
              cellValue: isSingleCell ? worksheet.getRange(intersections[0].shortAddress).load(['values']) : undefined
            }
          });
        }
        return filteredLookups;
      }, []);

      if (needToLoadValues) {
        await ctx.sync();
        lookups.forEach(({ selection }) => selection.cellValue = asString(selection.cellValue?.values[0][0]));
      }

      return lookups;
    });
  }

  getNamedRangeAddress(name, withId = false) {
    const range = this.namedRanges.find(range => range.name === name);
    return range?.toRangeAddress();
  }



  getWorksheet({ name, id }) {
    const result = this.worksheets.find(sheet => (id && sheet.id === id) || (name && sheet.name === name));
    return result;
  }

  async runTaskAndRefreshRanges(task) {
    await task();
    await this.updateNamedRanges();
  }
}
